import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import Table, { TableRow, TableCell } from "@amzn/meridian/table";
import Column from "@amzn/meridian/column";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import Text from "@amzn/meridian/text";
import minusKnockoutTokens from "@amzn/meridian-tokens/base/icon/minus-knockout";
import plusKnockoutTokens from "@amzn/meridian-tokens/base/icon/plus-knockout";
import incomingScipForecastsShipmentsTableColumns from "../../data/incoming-scip-forecast-table-columns";
import { getSummaryQuantity } from "../../utilities/parent-child-conversion";

const IncomingShipmentsScipForecastsTable = (props) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const { scipForecastsDetailed, manifestedVrids } = props;

  // Compute summary rows from detailed rows
  const summaryData = useMemo(() => {
    if (!scipForecastsDetailed) return [];
    
    // Group by date and sourceType
    const groupedData = scipForecastsDetailed.reduce((acc, detail) => {
      const key = `${detail.appointmentDate}_${detail.sourceType}`;
      if (!acc[key]) {
        acc[key] = {
          id: key,
          appointmentTime: detail.appointmentTime,
          appointmentDate: detail.appointmentDate,
          sourceType: detail.sourceType,
          expectedUnits: 0,
          scheduledUnits: 0,
          unscheduledUnits: 0,
          dockReceived: 0,
          time_zone: detail.time_zone
        };
      }
      
      // Sum up the values - use getSummaryQuantity for child quantities except for forecast
      acc[key].expectedUnits += detail.expectedUnits || 0; // Keep forecast as raw value
      acc[key].scheduledUnits += getSummaryQuantity(detail, 'scheduledUnits', 'childScheduledUnits');
      acc[key].unscheduledUnits += getSummaryQuantity(detail, 'unscheduledUnits', 'childUnscheduledUnits');
      acc[key].dockReceived += getSummaryQuantity(detail, 'dockReceived', 'childDockReceived');
      
      return acc;
    }, {});
    
    // Convert to array and sort by date
    return Object.values(groupedData).sort(
      (a, b) => new Date(a.appointmentDate) - new Date(b.appointmentDate)
    );
  }, [scipForecastsDetailed]);

  const updateExpandedRows = (date, ibType) => {
    setExpandedRows((current) => {
      const newMap = new Map(current);
      const key = `${date}-${ibType}`;
      if (newMap.has(key)) {
        newMap.delete(key);
      } else {
        newMap.set(key, true);
      }
      return newMap;
    });
  };

  const renderRows = () => {
    return summaryData.map((summary) => {
      const isExpanded = expandedRows.has(
        `${summary.appointmentTime}-${summary.sourceType}`
      );

      const tempZoneRows = isExpanded
        ? scipForecastsDetailed.filter(
            (detail) =>
              detail.appointmentDate === summary.appointmentDate &&
              detail.sourceType === summary.sourceType
          )
        : [];

      const allRows = [
        {
          date: summary.appointmentTime,
          ibType: summary.sourceType,
          tempZone: "TOTAL",
          forecast: summary.expectedUnits,
          scheduled: summary.scheduledUnits,
          unscheduled: summary.unscheduledUnits,
          dockReceived: summary.dockReceived || 0,
          isSummaryRow: true,
          isFirstRow: true,
        },
        ...tempZoneRows.map((detail) => ({
          date: detail.appointmentTime,
          ibType: detail.sourceType,
          tempZone: detail.tempZone,
          forecast: detail.expectedUnits, // Keep forecast as raw value
          scheduled: getSummaryQuantity(detail, 'scheduledUnits', 'childScheduledUnits'),
          unscheduled: getSummaryQuantity(detail, 'unscheduledUnits', 'childUnscheduledUnits'),
          dockReceived: getSummaryQuantity(detail, 'dockReceived', 'childDockReceived'),
          isSummaryRow: false,
          isFirstRow: false,
        })),
      ];

      const visibleDetailRows = isExpanded ? allRows : [allRows[0]];

      return visibleDetailRows.map((row, rowIndex) => {
        const actualRowSpan = isExpanded
          ? visibleDetailRows.filter(
              (r) => r.date === row.date && r.ibType === row.ibType
            ).length
          : 1;

        return (
          <TableRow
            key={`${row.date}-${row.ibType}-${rowIndex}`}
            style={row.isSummaryRow ? { fontWeight: "bold" } : {}}
          >
            {row.isFirstRow ? (
              <TableCell
                width={60}
                rowSpan={actualRowSpan}
                alignmentHorizontal="center"
              >
                <Button
                  type="icon"
                  size="small"
                  onClick={() => updateExpandedRows(row.date, row.ibType)}
                >
                  {isExpanded ? (
                    <Icon tokens={minusKnockoutTokens} color="secondary">
                      Collapse rows
                    </Icon>
                  ) : (
                    <Icon tokens={plusKnockoutTokens} color="secondary">
                      Expand rows
                    </Icon>
                  )}
                </Button>
              </TableCell>
            ) : null}
            {row.isFirstRow ? (
              <TableCell
                width={95}
                rowSpan={actualRowSpan}
                alignmentHorizontal="center"
              >
                <Column spacingInset="200 none none">{row.date}</Column>
              </TableCell>
            ) : null}
            {row.isFirstRow ? (
              <TableCell
                width={95}
                rowSpan={actualRowSpan}
                alignmentHorizontal="center"
              >
                {row.ibType}
                        {row.ibType === "Transship" &&
                          manifestedVrids?.length > 0 && (
                            <Text type="b100" color="secondary">
                              {manifestedVrids.find(
                                (item) => item.date === row.date
                              )?.manifestedVridsCount !== undefined &&
                                `${
                                  manifestedVrids.find(
                                    (item) => item.date === row.date
                                  ).manifestedVridsCount
                                } out of ${
                                  manifestedVrids.find(
                                    (item) => item.date === row.date
                                  ).totalVridsCount
                                } manifested`}
                            </Text>
                          )}
              </TableCell>
            ) : null}
            <TableCell width={95} alignmentHorizontal="center">
              {row.tempZone}
            </TableCell>
            <TableCell width={95} alignmentHorizontal="center">
              {row.forecast.toLocaleString()}
            </TableCell>
            <TableCell width={95} alignmentHorizontal="center">
              {row.scheduled.toLocaleString()}
            </TableCell>
            <TableCell width={95} alignmentHorizontal="center">
              {row.unscheduled.toLocaleString()}
            </TableCell>
            <TableCell width={170} alignmentHorizontal="center">
              {row.dockReceived.toLocaleString()}
            </TableCell>
          </TableRow>
        );
      });
    });
  };

  return (
    <Table headerRows={1} showDividers={true}>
      <TableRow>
        <TableCell width={60}>
          {/* Empty cell for expand/collapse icons */}
        </TableCell>
        {incomingScipForecastsShipmentsTableColumns.map((header) => (
          <TableCell
            key={header.id}
            sortColumn={header.id}
            width={header.width}
            alignmentHorizontal={header.alignment}
          >
            {header.label}
          </TableCell>
        ))}
      </TableRow>
      {renderRows()}
    </Table>
  );
};

IncomingShipmentsScipForecastsTable.propTypes = {
  scipForecastsDetailed: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    appointmentTime: PropTypes.string,
    appointmentDate: PropTypes.string,
    sourceType: PropTypes.string,
    tempZone: PropTypes.string,
    expectedUnits: PropTypes.number,
    scheduledUnits: PropTypes.number,
    unscheduledUnits: PropTypes.number,
    dockReceived: PropTypes.number,
    shipmentCount: PropTypes.number,
    time_zone: PropTypes.string
  })).isRequired,
  manifestedVrids: PropTypes.arrayOf(PropTypes.object)
};

export default IncomingShipmentsScipForecastsTable;
